import { useEffect, useState } from "react";
import { Link, NavLink, Outlet, useLocation } from "react-router-dom";
import { ArrowIcon, CloseIcon, FacebookIcon, InstagramIcon, MailIcon, MenuIcon, PhoneIcon, PinIcon } from "./Icons";
import { BOOKING_URL, EMAIL, MAP_URL, navigation, pageMetadata, PHONE_DISPLAY, PHONE_HREF } from "../site";

function Metadata() {
  const { pathname } = useLocation();

  useEffect(() => {
    const normalized = pathname === "/" ? "/" : `${pathname.replace(/\/+$/, "")}/`;
    const metadata = pageMetadata[normalized] ?? {
      title: "Nadia's Salon | Englishtown, NJ",
      description: "Hair, color, treatment, makeup, waxing, and wedding services from Nadia's Salon.",
    };
    document.title = metadata.title;
    document.querySelector<HTMLMetaElement>('meta[name="description"]')?.setAttribute("content", metadata.description);
    document.querySelector<HTMLLinkElement>('link[rel="canonical"]')?.setAttribute("href", `https://nadiashair.com${normalized}`);
    window.scrollTo({ top: 0, behavior: "instant" });
  }, [pathname]);

  return null;
}

export function Layout() {
  const [open, setOpen] = useState(false);
  const { pathname } = useLocation();

  useEffect(() => setOpen(false), [pathname]);
  useEffect(() => {
    if (!open) return;
    const close = (event: KeyboardEvent) => event.key === "Escape" && setOpen(false);
    document.addEventListener("keydown", close);
    document.body.classList.add("menu-open");
    return () => {
      document.removeEventListener("keydown", close);
      document.body.classList.remove("menu-open");
    };
  }, [open]);

  return (
    <div className="site-shell">
      <Metadata />
      <div className="utility-bar">
        <div className="container utility-bar__inner">
          <a href={MAP_URL} target="_blank" rel="noreferrer"><PinIcon />14 Wilson Avenue, Suite 3 · Englishtown, NJ</a>
          <a href={PHONE_HREF}><PhoneIcon />{PHONE_DISPLAY}</a>
        </div>
      </div>
      <header className="site-header">
        <div className="container site-header__inner">
          <Link className="brand" to="/" aria-label="Nadia's Salon home">
            <img src="/images/nadias-salon-logo.png" alt="Nadia's Salon" />
          </Link>
          <nav className="desktop-nav" aria-label="Primary navigation">
            {navigation.map((item) => (
              <NavLink key={item.path} to={item.path} end={item.path === "/"}>{item.label}</NavLink>
            ))}
          </nav>
          <a className="button button--small header-booking" href={BOOKING_URL} target="_blank" rel="noreferrer">
            Book online <ArrowIcon />
          </a>
          <button className="menu-button" type="button" aria-label={open ? "Close menu" : "Open menu"} aria-expanded={open} aria-controls="mobile-menu" onClick={() => setOpen((value) => !value)}>
            {open ? <CloseIcon /> : <MenuIcon />}
          </button>
        </div>
        <div id="mobile-menu" className={`mobile-panel${open ? " is-open" : ""}`} aria-hidden={!open} hidden={!open}>
          <nav aria-label="Mobile navigation">
            {navigation.map((item) => (
              <NavLink key={item.path} to={item.path} end={item.path === "/"}>{item.label}</NavLink>
            ))}
          </nav>
          <a className="button button--full" href={BOOKING_URL} target="_blank" rel="noreferrer">Book your appointment <ArrowIcon /></a>
          <a className="mobile-phone" href={PHONE_HREF}><PhoneIcon />{PHONE_DISPLAY}</a>
        </div>
      </header>

      <main id="main-content">
        <Outlet />
      </main>

      <footer className="site-footer">
        <div className="container footer-grid">
          <div className="footer-brand">
            <img src="/images/nadias-salon-logo.png" alt="Nadia's Salon" />
            <p>Attentive hair, color, makeup, and special-event beauty in Englishtown, New Jersey.</p>
            <div className="social-links">
              <a href="https://www.instagram.com/nadiasfamilysalon/" target="_blank" rel="noreferrer" aria-label="Nadia's Salon on Instagram"><InstagramIcon /></a>
              <a href="https://www.facebook.com/nadiassalon" target="_blank" rel="noreferrer" aria-label="Nadia's Salon on Facebook"><FacebookIcon /></a>
            </div>
          </div>
          <div>
            <h2>Visit</h2>
            <a className="footer-contact" href={MAP_URL} target="_blank" rel="noreferrer"><PinIcon /><span>14 Wilson Avenue, Suite 3<br />Englishtown, NJ 07726</span></a>
            <a className="footer-contact" href={PHONE_HREF}><PhoneIcon /><span>{PHONE_DISPLAY}</span></a>
            <a className="footer-contact" href={`mailto:${EMAIL}`}><MailIcon /><span>{EMAIL}</span></a>
          </div>
          <div>
            <h2>Explore</h2>
            <div className="footer-nav">
              {navigation.slice(1).map((item) => <Link key={item.path} to={item.path}>{item.label}</Link>)}
            </div>
          </div>
          <div className="footer-booking">
            <p className="eyebrow eyebrow--light">Ready when you are</p>
            <h2>Make time for your best hair.</h2>
            <a className="button button--light" href={BOOKING_URL} target="_blank" rel="noreferrer">Book online <ArrowIcon /></a>
          </div>
        </div>
        <div className="container footer-bottom">
          <p>© {new Date().getFullYear()} Nadia's Salon. All rights reserved.</p>
          <p>Englishtown, New Jersey</p>
        </div>
      </footer>
    </div>
  );
}
